import React, {useCallback, useMemo} from 'react';
import CustomizedIconButton from '../IconButton/IconButton';
import {useTranslation} from 'react-i18next';
import {PauseCircleFilledRounded, PlayCircleFilledRounded } from '@material-ui/icons';
import {ReactComponent as StopCircleFilledRounded} from '../../assets/payments/stopButton.svg';
import {CircularProgress} from '@material-ui/core';
import classNames from 'classnames';
import {makeStyles} from '@material-ui/core/styles';
import {Colors} from '../../styles/Colors';
import {
    initialState,
    PlayingFile,
    RecordingsPlayingState,
    RecordingsPlayingStatus
} from '../../store/reducers/calls/reducer';
import {useDispatch, useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import {actions} from '../../store';
import {usePermissionContext} from '../../hooks/usePermissions';
import {PermissionType} from '../../store/types/Permission';

export type PlayFileButtonProps = {
    files: PlayingFile[];
    customClasses?: {
        initial?: string,
        beforePlaying?: string,
        playing?: string,
        container?: string
    };
    dataQa?: string;
    dataTestId?: string;
    skipPermission?: boolean;
    customPlayText?: string;
    customStopText?: string;
    stopPlayingInsteadOfPause?: boolean;
    disabled?: boolean;
};

export const useStyles = makeStyles(() => ({
    playPauseButtonBase: {
        '& svg': {
            color: `${Colors.Link}`,
        },
        '&:hover': {
            background: `${Colors.Gray2}!important`,
            borderRadius: 24
        },
    },

    playPauseButtonLoading: {
        color: Colors.Primary2,
        '& .MuiCircularProgress-svg': {
            color: Colors.Primary2
        },
    },

    playClicked: {
        '& svg': {
            color: `${Colors.Primary2} !important`,
        },
    },

    disabledState: {
        cursor: 'unset',
        opacity: 0.6
    },

    stopButtonStyle: {
        color: Colors.Gray5,

        '& svg': {
            color: `${Colors.Gray5}`,
        },
    }
}));

const PlayFileButton: React.FC<PlayFileButtonProps> = ({
    files,
    customClasses,
    dataQa,
    dataTestId = '',
    skipPermission = false,
    customPlayText,
    customStopText,
    stopPlayingInsteadOfPause = false,
    disabled = false,
}) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const recordingsPlayer: RecordingsPlayingState = useSelector<ReduxState, RecordingsPlayingState>(
        (state) => state.calls.recordingsPlayer,
    );
    
    const permission = skipPermission ? PermissionType.Visible : usePermissionContext();

    if(permission === PermissionType.Hidden) {
        return (<></>);
    }

    const isTheCurrentFile = useMemo(() => {
        if(!(recordingsPlayer?.currentFile)) return false;
        for(const f of files) {
            if(f && f.call_recording_id === recordingsPlayer.currentFile.call_recording_id
                && f.i_xdr === recordingsPlayer.currentFile.i_xdr)
            {
                return true;
            }
        }
        return false;
    }, [files, recordingsPlayer]);
    
    const downloading = useMemo(() => {
        return isTheCurrentFile && 
            (recordingsPlayer.status == RecordingsPlayingStatus.downloading
            || recordingsPlayer.status == RecordingsPlayingStatus.downloadedNotStartedPlaying);
    }, [isTheCurrentFile, recordingsPlayer]);

    const playClicked = useMemo(() => {
        return isTheCurrentFile && 
            (recordingsPlayer.status == RecordingsPlayingStatus.playing);
    }, [isTheCurrentFile, recordingsPlayer]);
    
    const onClickPlay = useCallback(() => {
        if(recordingsPlayer.status === RecordingsPlayingStatus.notDefined || 
            recordingsPlayer.status === RecordingsPlayingStatus.playing ||
            (recordingsPlayer.status === RecordingsPlayingStatus.finished && !isTheCurrentFile) ||
            (recordingsPlayer.status === RecordingsPlayingStatus.paused && !isTheCurrentFile)) {

            if(!files || !files.length) return;
            const file = files[0]; 
            
            dispatch(actions.updateRecordingsPlayingState({
                ...recordingsPlayer,
                status: RecordingsPlayingStatus.downloading,
                currentFile: file,
                currentBlob: null
            }));

            const callback = (url: string) => {
                setTimeout(() => {
                    dispatch(actions.updateRecordingsPlayingState({
                        ...recordingsPlayer,
                        currentFile: file,
                        status: RecordingsPlayingStatus.downloadedNotStartedPlaying,
                        currentBlob: url
                    }));
                }, 1);
            };

            const restrictedCallback = () => {
                setTimeout(() => {
                    dispatch(actions.updateRecordingsPlayingState({
                        ...initialState.recordingsPlayer,
                        status: RecordingsPlayingStatus.dowloadingRestricted,
                    }));
                }, 1);
            };
            
            const restrictedCallbackAllowsToDownloadAnotherFile = () => {
                setTimeout(() => {
                    dispatch(actions.updateRecordingsPlayingState({
                        ...initialState.recordingsPlayer,
                        status: RecordingsPlayingStatus.notDefined,
                    }));
                }, 1);
            };

            if(file?.customDownloadFileAction)
            {
                file.customDownloadFileAction(callback, restrictedCallbackAllowsToDownloadAnotherFile);
            } 
            else {
                dispatch(
                    actions.getCallHistoryFileAsBlob.request({
                        i_xdr: file.i_xdr,
                        call_recording_id: file.call_recording_id,
                        callback: callback,
                        restrictedCallback: restrictedCallback,
                    }),
                );
            }

        } else if ((recordingsPlayer.status === RecordingsPlayingStatus.paused && isTheCurrentFile)
                || (recordingsPlayer.status === RecordingsPlayingStatus.finished && isTheCurrentFile)) {
                dispatch(actions.updateRecordingsPlayingState({
                    ...recordingsPlayer,
                    status: RecordingsPlayingStatus.downloadedNotStartedPlaying,
                }));
        }
    }, [isTheCurrentFile, recordingsPlayer, files]);

    const onClickPause = useCallback(() => {
        dispatch(actions.updateRecordingsPlayingState({
            ...recordingsPlayer,
            status: RecordingsPlayingStatus.paused
        }));
    }, [isTheCurrentFile, recordingsPlayer]);

    const buttonDisabled = useMemo(() => {
        if(disabled)
            return true;
        if(recordingsPlayer.status === RecordingsPlayingStatus.dowloadingRestricted
            || permission !== PermissionType.Visible)
            return true;
        return files?.length == 1 && files[0].i_xdr === 0;
    }, [recordingsPlayer, permission, files]);

    if(downloading) {
        return (
            <CircularProgress className={classNames(classes.playPauseButtonBase, classes.playPauseButtonLoading, customClasses?.beforePlaying)} />
        );
    }
    
    return (<CustomizedIconButton
        tooltipText={buttonDisabled
                ? undefined
            : playClicked 
                ? (customStopText ?? t('tooltips:callSettings.pauseAudio'))
                : (customPlayText ?? t('tooltips:callSettings.playAudio'))}
        dataQa={dataQa}
        dataTestId={dataTestId}
        onClick={() => {
            if(!playClicked) {
                onClickPlay();
            } else {
                onClickPause();
            }
        }}
        above={true}
        className={classNames(classes.playPauseButtonBase,
            customClasses?.container,
            playClicked && classes.playClicked, 
            playClicked && customClasses?.playing,
            !playClicked && customClasses?.initial,
            buttonDisabled && classes.disabledState,
            stopPlayingInsteadOfPause && playClicked && classes.stopButtonStyle)}
        disabled={buttonDisabled}
        skipPermission
    >
        {
        playClicked ? stopPlayingInsteadOfPause ? (
            <StopCircleFilledRounded />
        ) : (
            <PauseCircleFilledRounded/>
        ) : (
            <PlayCircleFilledRounded/>
        )}
    </CustomizedIconButton>);
};

export default PlayFileButton;